import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, Megaphone, TrendingUp, DollarSign, Sparkles, ListTodo } from 'lucide-react';
import { KpiCard } from '@/components/KpiCard';
import { PageHeader } from '@/components/PageHeader';
import { AiHealthCard } from '@/components/AiHealthCard';
import { getClients, getCampaigns, getAnalyticsSummary, getAnalyticsTimeseries, getAnalyticsChannels, getTasks } from '@/lib/api';

const COLORS = ['#2BAE9B', '#2A6FDB', '#D97706', '#DC2626'];

const fadeIn = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

export default function AdminDashboardPage() {
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [timeseries, setTimeseries] = useState([]);
  const [channelData, setChannelData] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sumRes, clRes, campRes, tsRes, chRes, taskRes] = await Promise.all([
        getAnalyticsSummary(),
        getClients(),
        getCampaigns(),
        getAnalyticsTimeseries(),
        getAnalyticsChannels(),
        getTasks()
      ]);
      setSummary(sumRes.data);
      setClients(clRes.data);
      setCampaigns(campRes.data);
      setTimeseries(tsRes.data);
      setChannelData(chRes.data);
      setTasks(taskRes.data);
    } catch (err) {
      console.error('Admin dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-sm text-slate-500">Dashboard wird geladen...</div></div>;
  }

  const openTasks = tasks.filter(t => t.status !== 'erledigt').length;
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <PageHeader title="Admin Überblick" description="Gesamte Agentur-Performance auf einen Blick" />

      <motion.div variants={fadeIn} className="mb-4">
        <AiHealthCard />
      </motion.div>

      {/* KPI Row */}
      <motion.div variants={fadeIn} className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <KpiCard label="Aktive Kunden" value={clients.filter(c => c.status === 'active').length} testId="kpi-admin-clients" />
        <KpiCard label="Aktive Kampagnen" value={activeCampaigns} testId="kpi-admin-campaigns" />
        <KpiCard label="Gesamt Installs" value={summary?.total_installs || 0} testId="kpi-admin-installs" />
        <KpiCard label="ROAS" value={summary?.roas || 0} suffix="x" testId="kpi-admin-roas" />
      </motion.div>

      <div className="grid md:grid-cols-12 gap-4 sm:gap-6 mb-6">
        {/* Performance Trend */}
        <motion.div variants={fadeIn} className="md:col-span-8 rounded-xl border border-slate-200 bg-white p-4 sm:p-6">
          <h3 className="font-display text-base font-semibold text-slate-900 mb-4">Performance Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={timeseries.slice(-30)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => v?.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
              <Line type="monotone" dataKey="installs" stroke="#2BAE9B" strokeWidth={2} dot={false} name="Installs" />
              <Line type="monotone" dataKey="revenue" stroke="#2A6FDB" strokeWidth={2} dot={false} name="Revenue (€)" />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Channel Distribution */}
        <motion.div variants={fadeIn} className="md:col-span-4 rounded-xl border border-slate-200 bg-white p-4 sm:p-6">
          <h3 className="font-display text-base font-semibold text-slate-900 mb-4">Kanal-Verteilung (Spend)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={channelData} dataKey="spend" nameKey="channel" cx="50%" cy="50%" outerRadius={75} innerRadius={45}>
                {channelData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-2">
            {channelData.map((ch, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-slate-600">{ch.channel}</span>
                </div>
                <span className="text-slate-900 font-medium tabular-nums">€{ch.spend?.toLocaleString('de-DE')}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Recent activity */}
      <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Clients */}
        <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold text-slate-900">Neueste Kunden</h3>
            <span className="text-xs text-slate-500">{clients.length} gesamt</span>
          </div>
          <div className="divide-y divide-slate-50">
            {clients.slice(0, 5).map((cl, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                <div>
                  <p className="text-sm font-medium text-slate-900">{cl.name}</p>
                  <p className="text-xs text-slate-500">{cl.industry} | {cl.app_name}</p>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cl.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                  {cl.status === 'active' ? 'Aktiv' : 'Pausiert'}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Open Tasks */}
        <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold text-slate-900">Offene Tasks</h3>
            <span className="text-xs text-slate-500">{openTasks} offen</span>
          </div>
          <div className="divide-y divide-slate-50">
            {tasks.filter(t => t.status !== 'erledigt').slice(0, 5).map((t, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                <div>
                  <p className="text-sm font-medium text-slate-900">{t.title}</p>
                  <p className="text-xs text-slate-500">{t.assignee} | Fällig: {t.due_date}</p>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${{
                  urgent: 'bg-red-50 text-red-700',
                  high: 'bg-amber-50 text-amber-700',
                  medium: 'bg-sky-50 text-sky-700',
                  low: 'bg-slate-100 text-slate-600'
                }[t.priority] || 'bg-slate-100 text-slate-600'}`}>
                  {t.priority}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
